'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

function ImageDataCard({
    src,
    alt,
    className,
    imageClassName,
}: Readonly<{
    src: string;
    alt: string;
    className: string;
    imageClassName?: string;
}>) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        setImageLoaded(false);
    }, [src]);

    useEffect(() => {
        const image = imageRef.current;
        if (!image) {
            return;
        }

        if (image.complete && image.naturalWidth > 0) {
            setImageLoaded(true);
        }
    }, [src]);

    return (
        <div className={className}>
            {!imageLoaded && (
                <div className="absolute inset-0 z-10 overflow-hidden rounded-[16px] bg-white/5 before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent" />
            )}
            <Image
                ref={imageRef}
                src={src}
                alt={alt}
                fill
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
                className={
                    imageLoaded
                        ? (imageClassName ?? 'object-cover')
                        : `${imageClassName ?? 'object-cover'} opacity-0`
                }
            />
        </div>
    );
}

export default function ImageData() {
    const { t } = useTranslation('image-data');

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const isAtTop = el.scrollTop <= 0;
        const isAtBottom =
            Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) <= 1;

        if (isAtTop && e.deltaY < 0) return;
        if (isAtBottom && e.deltaY > 0) return;

        e.stopPropagation();
    };

    return (
        <section className="relative z-10 flex w-full flex-col items-center bg-black px-[32px] text-white">
            <div className="tablet:max-w-[87.5vw] desktop:max-w-[1160px] tablet:gap-[32px] desktop:gap-[48px] flex w-[87.5vw] max-w-[480px] flex-col gap-[24px]">
                <div className="tablet:text-center j-h4 tablet:j-h3 text-start">
                    <p>{t('title.part1')}</p>
                    <p>
                        <span className="bg-[linear-gradient(90deg,#F7A1FF,#7AAFFF)] bg-clip-text text-transparent">
                            {t('title.part2')}
                        </span>
                    </p>
                </div>

                <div className="tablet:gap-[8px] relative flex w-full flex-col gap-[4px]">
                    {/* Images Layout Container */}
                    <div className="tablet:flex-row tablet:gap-[8px] relative flex w-full flex-col gap-[4px]">
                        {/* Left Original Image (Desktop) / Top (Mobile) */}
                        <ImageDataCard
                            src={getStaticAssetUrl(
                                '/home/img-image-data-1.webp',
                            )}
                            alt="Original render"
                            className="tablet:w-2/3 relative aspect-[16/10] w-full overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                            imageClassName="object-cover"
                        />

                        {/* Right Info Images (Desktop) / Middle (Mobile) */}
                        <div className="tablet:flex-col tablet:w-1/3 tablet:gap-[8px] flex h-auto w-full flex-row gap-[4px]">
                            <ImageDataCard
                                src={getStaticAssetUrl(
                                    '/home/img-image-data-2.webp',
                                )}
                                alt="Depth map"
                                className="tablet:w-full tablet:flex-1 relative aspect-[16/10] w-1/2 overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                                imageClassName="object-cover filter grayscale"
                            />
                            <ImageDataCard
                                src={getStaticAssetUrl(
                                    '/home/img-image-data-3.webp',
                                )}
                                alt="Segmentation map"
                                className="tablet:w-full tablet:flex-1 relative aspect-[16/10] w-1/2 overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                                imageClassName="object-cover"
                            />
                        </div>
                    </div>

                    {/* JSON Data Block */}
                    <div className="tablet:h-[240px] flex h-[200px] w-full flex-col overflow-hidden rounded-[4px] border border-white/10 bg-[#1A1A1A]">
                        <div className="j-t3 flex-shrink-0 bg-[#212121] px-[16px] py-[8px] text-white">
                            robot_view_camera_params
                        </div>
                        <div
                            className="j-t3 flex-1 overflow-y-auto overflow-x-hidden whitespace-pre-wrap px-[16px] py-[8px] text-[#9B9A9D]"
                            onWheel={handleWheel}
                        >
                            {`{
  "camera_name": "Robot-View",
  "timestamp_seconds": 222.375000,

  "location": {
    "x": -2.763041,
    "y": -2.039186,
    "z": 1.500000
  },
  
  "quaternion": {
    "qw": 0.6509729028,
    "qx": 0.3388750553,
    "qy": -0.3136486709,
    "qz": -0.6025134921
  },
  
  "robot view_parameters": {
    "class_name": "RobotView Parameters",
    "extrinsic": [
      0.07720405608415604,
      -0.5718645453453064,
      0.816707193851471,
      -0.0,
      -0.9970153570175171,
      -0.04428243264555931,
      0.0632418692111969,
      0.0,
      3.725290742551124e-09,
      -0.8191520571708679,
      -0.5735765099525452,
      -0.0,
      -1.8197823762893677,
      -0.4416574239730835,
      3.245922565460205,
      1.0
    ],
    "intrinsic": {
      "height": 2160,
      "intrinsic_matrix": [
        2306.217300415039,
        0.0,
        0.0,
        0.0,
        2306.217300415039,
        0.0,
        2088.0,
        1080.0,
        1.0
      ],
      "width": 4176
    },
    "version_major": 1,
    "version_minor": 0
  },
  
  "camera_details": {
    "sensor_width_mm": 36.0,
    "sensor_height_mm": 27.0,
    "focal_length_mm": 19.881183624267578,
    "sensor_fit": "HORIZONTAL",
    "fx_pixels": 2306.217300415039,
    "fy_pixels": 2306.217300415039,
    "cx_pixels": 2088.0,
    "cy_pixels": 1080.0
  },
  
  "notes": {
    "coordinate_system": "Extrinsic matrix uses computer vision convention (Y-up, -Z forward)",
    "extrinsic_format": "Column-major 4x4 matrix (world-to-camera transformation)",
    "intrinsic_format": "Row-major 3x3 matrix [fx, 0, 0, 0, fy, 0, cx, cy, 1]",
    "quaternion_order": "WXYZ (scalar first)",
    "location_units": "Blender units (meters)"
  }
}`}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
